import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { PortHookInterface } from "../portDetailsHook";
import { useOpenClose } from "../../../utils/hooks/OpenCloseHook";
import { useLogs } from "../logsHook";
import { areYouSure, wrapInDrawer } from "../../../utils/methods";
import { ProviderView } from "./ProviderView";
import BasicMenu from "../../DesignSystem/menu";
import { Icon } from "@iconify/react";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === "light" ? "rgb(55, 65, 81)" : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

interface ThisMenuProps {
  portProps: PortHookInterface;
  isBandwidthPage: boolean;
}

export default function OptionsMenu(props: ThisMenuProps) {
  const { portDetails: port, logs, rollService, cancelSubscription, listService, deleteOffNet } = props.portProps;
  const { isBandwidthPage } = props;
  const logsView = useOpenClose();
  const providerView = useOpenClose();
  const logList = useLogs(logs);

  if (isBandwidthPage || !port || port.bandwidth_id)
    return (
      <div className="top-right">
        <BasicMenu
          classNames="settingsIcon"
          content={<Icon icon={"fluent:settings-16-regular"} color="grey" width={25} />}
          menuItems={[]}
          disabled
          disabledTooltip={!isBandwidthPage ? "Cannot edit bandwidth port from this screen. Navigate to the bandwidth page to use the options." : ""}
        />
      </div>
    );

  const menuItems = [
    {
      content: "See History",
      action: () => {
        logsView.open();
      },
      disabled: false,
    },
    {
      content: "Provider View",
      action: () => {
        providerView.open();
      },
      disabled: !port.buyer_id,
    },
    {
      content: "New Listing",
      action: () => {
        listService.handlePutForSaleOpen(port);
      },
      disabled: !!port.buyer_id || !!port.cross_connect_port_id || !!port.listing_id,
    },
    {
      content: "Edit Listing",
      action: () => {
        listService.handleSaleEdit(port);
      },
      disabled: !!port.buyer_id || !!port.cross_connect_port_id || !port.listing_id,
    },
    {
      content: "Remove Listing",
      action: () => {
        areYouSure(() => listService.handleRemoveClick(port), `Remove listing ${port.service_description}?`);
      },
      disabled: !!port.buyer_id || !!port.cross_connect_port_id || !port.listing_id,
    },
    {
      content: "Cancel Service",
      action: () => {
        areYouSure(() => cancelSubscription.do(), `Cancel subscription ${port.port_description} ${port.port_name}?`);
      },
      disabled: !(port.parent_id && !port.buyer_id),
    },
    {
      content: "Delete Port",
      action: () => {
        areYouSure(() => deleteOffNet.do(), `Delete port ${port.port_description} ${port.port_name}`);
      },
      disabled: !props.portProps.canDeleteOffNet,
    },
  ];

  return (
    <div className="top-right">
      <BasicMenu classNames="settingsIcon" content={<Icon icon={"fluent:settings-16-regular"} color="white" width={25} />} menuItems={menuItems} />
      {wrapInDrawer(logList, logsView.isOpen, () => logsView.close(), true)}
      {wrapInDrawer(<ProviderView portProps={props.portProps} rollService={rollService} />, providerView.isOpen, () => providerView.close(), true)}
      {listService.SaleDrawerComponent}
    </div>
  );
}
